import type { DocxTableInfo } from "./collectDocxFormValues";
/**
 * Restore form values from DOCX format data back to the DOM
 */
export function restoreDocxFormValues(
  data: DocxTableInfo[],
  containerSelector: string = ".preview-form-content"
): void {
  const container = document.querySelector(containerSelector);
  if (!container || !data || !data.length) {
    console.warn("restoreDocxFormValues: Container not found or no data", {
      container: !!container,
      hasData: !!data.length,
    });
    return;
  }

  console.log("Restoring DOCX form values:", data.length, "tables");

  // Get all table IDs present in the current section
  const sectionTableIds = new Set<string>();
  container.querySelectorAll("[data-table-id]").forEach((el) => {
    const tableId = el.getAttribute("data-table-id");
    if (tableId) sectionTableIds.add(tableId);
  });

  data.forEach((table) => {
    // Only process this table if it exists in the current section
    if (!sectionTableIds.has(table.tableId)) {
      console.log(
        `Skipping table ${table.tableId} - not found in current section`
      );
      return;
    }

    table.rows.forEach((row, index) => {
      // Find the row element by data-row-id (now div instead of tr)
      let rowElement = container.querySelector(
        `div[data-row-id="${row.rowId}"]`
      );
      if (!rowElement && index >= 1) {
        // Row doesn't exist - need to create it dynamically
        const previousRowId = table.rows[index - 1].rowId;
        const previousRow = container.querySelector(
          `div[data-row-id="${previousRowId}"]`
        );
        if (!previousRow) {
          console.warn(
            `Cannot create row ${row.rowId} - previous row ${previousRowId} not found`
          );
          return;
        }
        // Check if the row contains InlineText fields
        const hasInlineText = previousRow.querySelector(
          '[data-item-type="InlineText"]'
        );

        if (hasInlineText) {
          // For InlineText, only clone the previous row (single row copy)
          const clonedRow = previousRow.cloneNode(true) as HTMLElement;

          // Update data-row-id for the cloned row
          clonedRow.setAttribute("data-row-id", row.rowId);

          // Clear input values in cloned row
          clonedRow
            .querySelectorAll("input, textarea, select")
            .forEach((input) => {
              if (input instanceof HTMLInputElement) {
                if (input.type === "checkbox" || input.type === "radio") {
                  input.checked = false;
                } else {
                  input.value = "";
                }
              } else if (
                input instanceof HTMLTextAreaElement ||
                input instanceof HTMLSelectElement
              ) {
                input.value = "";
              }
            });


          // Update rowElement reference to the newly created row
          rowElement = clonedRow;
          // Remove buttons from cloned rows
            clonedRow
              .querySelectorAll("button")
              .forEach((btn) => btn.remove());
            clonedRow.querySelectorAll("button").forEach((btn) => btn.remove());

            // Insert the cloned rows after the previous row
            previousRow.after(clonedRow);

            // Update rowElement reference to the newly created row
            rowElement = clonedRow;
            console.log(`Created new row: ${row.rowId}`);
        } else {
          // For non-InlineText fields, clone the previous row and the row above it (two row copy)
          const rowAbovePrevious = previousRow.previousElementSibling;

          if (rowAbovePrevious) {
            const clonedRowAbove = rowAbovePrevious.cloneNode(
              true
            ) as HTMLElement;
            const clonedRow = previousRow.cloneNode(true) as HTMLElement;
            const previousRowId = clonedRow.getAttribute("data-row-id");

            // Update data-row-id for the cloned rows
            clonedRowAbove.setAttribute("data-row-id",  crypto.randomUUID());
            clonedRow.setAttribute("data-row-id", row.rowId);

            // // Update all data-id attributes in the cloned row
            // clonedRow.querySelectorAll('[data-id]').forEach((el) => {
            //   const oldId = el.getAttribute('data-id');
            //   if (oldId) {
            //     const newId = oldId.replace(previousRowId, row.rowId);
            //     el.setAttribute('data-id', newId);
            //   }
            // });

            // Clear input values in cloned rows
            clonedRowAbove
              .querySelectorAll("input, textarea, select")
              .forEach((input) => {
                if (input instanceof HTMLInputElement) {
                  if (input.type === "checkbox" || input.type === "radio") {
                    input.checked = false;
                  } else {
                    input.value = "";
                  }
                } else if (
                  input instanceof HTMLTextAreaElement ||
                  input instanceof HTMLSelectElement
                ) {
                  input.value = "";
                }
              });

            clonedRow
              .querySelectorAll("input, textarea, select")
              .forEach((input) => {
                if (input instanceof HTMLInputElement) {
                  if (input.type === "checkbox" || input.type === "radio") {
                    input.checked = false;
                  } else {
                    input.value = "";
                  }
                } else if (
                  input instanceof HTMLTextAreaElement ||
                  input instanceof HTMLSelectElement
                ) {
                  input.value = "";
                }
              });

            // Remove buttons from cloned rows
            clonedRowAbove
              .querySelectorAll("button")
              .forEach((btn) => btn.remove());
            clonedRow.querySelectorAll("button").forEach((btn) => btn.remove());

            // Insert the cloned rows after the previous row
            previousRow.after(clonedRowAbove);
            clonedRowAbove.after(clonedRow);

            // Update rowElement reference to the newly created row
            rowElement = clonedRow;
            console.log(`Created new row: ${row.rowId}`);
          }
        }
      }

      if (!rowElement) {
        console.warn(`Row not found and could not be created: ${row.rowId}`);
        return;
      }
      const dataType = rowElement.querySelector("div[data-type]");
      if (dataType == null) {
        row.downloadItems.forEach((item) => {
          // Find the input element by data-id
          const input = rowElement.querySelector(`[data-id="${item.itemId}"]`);

          if (!input) {
            console.warn(`Input not found: ${item.itemId} in row ${row.rowId}`);
            return;
          }

          if (input instanceof HTMLInputElement) {
            if (input.type === "checkbox" || input.type === "radio") {
              input.checked =
                item.value != null &&
                item.value.toString().toLowerCase() == "true";
            } else {
              input.value = String(item.value);
            }
          } else if (input instanceof HTMLTextAreaElement) {
            input.value = String(item.value);
          } else if (input instanceof HTMLSelectElement) {
            input.value = String(item.value);
          }
        });
      } else {
        row.downloadItems.forEach((item) => {
          let items: string[] = [];
          if (
            Array.isArray(item.value) &&
            item.value.every((v) => typeof v === "string")
          ) {
            items = item.value;
          } else {
            items = JSON.parse(String(item.value)) as string[];
          }
          // Find the input element by data-id
          const inputs = rowElement.querySelectorAll(`input`);
          let index: number = 0;
          inputs.forEach((el) => {
            if (el instanceof HTMLInputElement) {
              el.value = items[index];
              index += 1;
            }
          });
        });
      }
    });
  });
  console.log("DOCX form values restored successfully");
}

export function incrementRowId(rowId: string): string {
  const ids = rowId.split("-");
  if(ids.length <= 2) {
    return `${rowId}-1`;
  }
  else {
    const id = ids.pop();
    const incrementedId = parseInt(id!, 10) + 2;
    return `${ids.join("-")}-${incrementedId}`;
  }
}